import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter } from 'rxjs/operators';
import { AuthorizeService } from 'src/api-authorization/authorize.service';
import { analyticsIdsFor, initialReferrer, isAnalyticsHost, routeTemplate, titleFor } from './analytics-helpers';

/** Event names sent to Google Analytics (GA4 recommended names where one fits). */
export type AnalyticsEvent = 'login' | 'file_download' | 'ais_live_check' | 'ai_assistant_question';

export type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

interface ClientConfig {
  googleAnalyticsMeasurementId?: string | null;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

// Turn on GA DebugView for this browser: open any page with ?analytics_debug=1 (=0 turns it off).
const DEBUG_KEY = 'lrm.analytics.debug';
// Events raised before we know whether analytics is on are held (briefly) rather than lost.
const MAX_QUEUED = 50;
// How long the first events wait for the signed-in user to be known.
const USER_WAIT_MS = 2000;

/**
 * Google Analytics 4, in production only. Starts once from AppComponent and stays off unless:
 * the page is not on a local or staging host, the browser does not send Global Privacy Control,
 * and the server returns a measurement ID (GET /api/ClientConfig; the ID is configured only on the
 * production App Service). Until then nothing is loaded from Google and nothing is sent.
 *
 * What is sent, and what is not:
 * - Page views on every in-app navigation, as the route template ("/contracts/view/:id") with a
 *   readable title ("Contracts › View"): never record ids, query strings or OIDC codes.
 * - A pseudonymous user id (SHA-256 of the internal user id), a short code for the person (user_ref,
 *   "U-05B9D4F8") and their role, so reports can count people, compare roles and, in Admin › Usage,
 *   show usage per person: only this app can turn a code back into a name. Never names or emails.
 * - A few events: login, file_download (every report/export), ais_live_check, ai_assistant_question.
 * - Google signals and ad personalisation are off.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService implements OnDestroy {
  private state: 'idle' | 'pending' | 'on' | 'off' = 'idle';
  private queue: Array<() => void> = [];
  private subscriptions: Subscription[] = [];
  private lastLocation = '';
  private debug = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private authorize: AuthorizeService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  /** Called once at startup. Safe to call again (no-op). */
  start(): void {
    if (this.state !== 'idle') {
      return;
    }
    const win = this.document.defaultView;
    if (!win || !isAnalyticsHost(win.location.hostname) || (win.navigator as any)?.globalPrivacyControl === true) {
      this.state = 'off';
      return;
    }
    this.state = 'pending';
    this.debug = this.readDebugFlag(win);

    // Subscribe before the first navigation finishes, so the landing page is counted too.
    this.subscriptions.push(this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.pageView(e.urlAfterRedirects)));

    this.subscriptions.push(this.http.get<ClientConfig>('/api/ClientConfig')
      .pipe(catchError(() => of(null)))
      .subscribe(config => {
        const id = config?.googleAnalyticsMeasurementId;
        if (!id) {
          this.state = 'off';
          this.queue = [];
          return;
        }
        this.load(win, id);
        // Identify the user before sending what was queued, so the landing page counts for them too;
        // never wait more than a moment for it.
        const timer = win.setTimeout(() => this.flush(), USER_WAIT_MS);
        this.trackUser(() => {
          win.clearTimeout(timer);
          this.flush();
        });
      }));
  }

  /** Sends an event (dropped where analytics is off). Parameters must never hold names, emails or free text. */
  event(name: AnalyticsEvent, params: AnalyticsParams = {}): void {
    this.whenOn(() => this.gtag('event', name, params));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  // ---- internals ----------------------------------------------------------------------------

  private load(win: Window, id: string): void {
    win.dataLayer = win.dataLayer || [];
    // gtag must push the arguments object itself, exactly as Google's snippet does.
    // eslint-disable-next-line prefer-rest-params
    win.gtag = function gtag() { win.dataLayer!.push(arguments); };
    win.gtag('js', new Date());
    win.gtag('config', id, {
      send_page_view: false, // sent on each in-app navigation instead, with a clean path
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      ...(this.debug ? { debug_mode: true } : {}),
    });

    const script = this.document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    this.document.head.appendChild(script);
  }

  private flush(): void {
    if (this.state !== 'pending') {
      return;
    }
    this.state = 'on';
    const queued = this.queue;
    this.queue = [];
    queued.forEach(send => send());
  }

  private pageView(url: string): void {
    const template = routeTemplate(this.router.routerState.snapshot.root, url);
    if (template.startsWith('/authentication')) {
      return; // sign-in plumbing (login callback, logout), not pages people use
    }
    this.whenOn(() => {
      const win = this.document.defaultView!;
      const location = win.location.origin + template;
      const referrer = this.lastLocation || initialReferrer(this.document.referrer, win.location.origin);
      const title = titleFor(template);
      // "set" makes every later event (downloads, live checks…) report this clean page, not the raw URL.
      this.gtag('set', { page_location: location, page_referrer: referrer, page_title: title });
      this.gtag('event', 'page_view', { page_location: location, page_referrer: referrer, page_title: title });
      this.lastLocation = location;
    });
  }

  /** Keeps GA's user id and role in step with who is signed in; calls ready once the first answer is applied. */
  private trackUser(ready: () => void): void {
    this.subscriptions.push(this.authorize.getUser()
      .pipe(distinctUntilChanged((a, b) => (a as any)?.sub === (b as any)?.sub))
      .subscribe(async user => {
        const sub: string | undefined = (user as any)?.sub;
        if (!sub) {
          this.gtag('set', { user_id: null });
          this.gtag('set', 'user_properties', { user_ref: null, user_role: null });
          ready();
          return;
        }
        const ids = await analyticsIdsFor(sub, this.document.defaultView?.crypto?.subtle).catch(() => null);
        if (ids) {
          this.gtag('set', { user_id: ids.userId });
        }
        this.gtag('set', 'user_properties', { user_ref: ids?.userRef ?? null, user_role: user?.role || 'User' });
        ready();
      }));
  }

  private whenOn(send: () => void): void {
    if (this.state === 'on') {
      send();
    } else if (this.state === 'pending' && this.queue.length < MAX_QUEUED) {
      this.queue.push(send);
    }
  }

  private gtag(...args: unknown[]): void {
    const win = this.document.defaultView;
    win?.gtag?.(...args);
    if (this.debug) {
      console.debug('[analytics]', ...args);
    }
  }

  private readDebugFlag(win: Window): boolean {
    try {
      const param = new URLSearchParams(win.location.search).get('analytics_debug');
      if (param === '1') {
        win.localStorage.setItem(DEBUG_KEY, '1');
      } else if (param === '0') {
        win.localStorage.removeItem(DEBUG_KEY);
      }
      return win.localStorage.getItem(DEBUG_KEY) === '1';
    } catch {
      return false;
    }
  }
}
